import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaCloud,
  FaServer,
  FaShieldAlt,
  FaCogs,
  FaDatabase,
  FaChartLine,
  FaUsers,
  FaLock,
  FaClock,
  FaGlobe,
  FaCheckCircle,
  FaArrowRight,
  FaRocket,
  FaLightbulb,
  FaCheckCircle as FaCheckCircleFilled
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../components/SEO/SEO';
import ServiceCard from '../components/ui/ServiceCard';
import TestimonialCarousel from '../components/ui/TestimonialCard';
import CaseStudyCard from '../components/ui/CaseStudyCard';
import BlogCard from '../components/BlogCard';
import AWSPartnerSection from '../components/sections/AWSPartnerSection';

import PartnersSection from '../components/sections/PartnersSection';
import HeroSimple from '../components/sections/HeroSimple';
import SupportChat from '../components/ui/SupportChat';
import contentData from '../data/contentData.json';
import { fadeIn, slideUp, slideRight, slideLeft, staggerContainer } from '../utils/animations';

// Import images directly
import cloudMigration from '../assets/images/aws-cloud-migration-services.webp';
import cloudSecurity from '../assets/images/aws-cloud-security-services.webp';
import costManagement from '../assets/images/aws-cost-management.webp';
import dataAnalytics from '../assets/images/aws-data-analytics-solutions.webp';
import generativeAI from '../assets/images/aws-generative-ai-solutions.webp';
import managedServices from '../assets/images/aws-managed-services-and-support.webp';
import vpc from '../assets/images/aws-virtual-private-cloud-vpc.webp';
import gcpImage from '../assets/images/google-cloud-platform-in-rajasthan-shanav-1728034805.webp';
import azureImage from '../assets/images/microsoft-azure-in-rajasthan-shanav-1728034816.webp';
import cloudComputingImage from '../assets/images/aboutimg.jpg';
import heroBanner from '../assets/images/cloud-computing-banner-1726449565.webp';
import testimonialBg from '../assets/images/growtika-wlqukvdhvqw-unsplash-1744027700.jpg';
import caseStudyImg from '../assets/images/cloud-computing-architecture-1-1280x720.jpg';
import ctaBanner from '../assets/images/cloud-consulting-banner.webp';

// Service image mapping
const serviceImageMap = {
  'cloud-migration': cloudMigration,
  'cloud-security': cloudSecurity,
  'cost-management': costManagement,
  'data-analytics': dataAnalytics,
  'generative-ai': generativeAI,
  'managed-services': managedServices,
  'vpc': vpc,
  'gcp-migration': gcpImage,
  'azure-migration': azureImage
};



const ServicesSection = () => {
  // Filter cloud services specifically from the aws-cloud-services category
  const cloudCategory = contentData.services.find(cat => cat.id === 'aws-cloud-services');
  
  const iconMap = {
    'cloud-migration': <FaCloud />,
    'cloud-security': <FaShieldAlt />,
    'cost-management': <FaChartLine />,
    'data-analytics': <FaDatabase />,
    'generative-ai': <FaRocket />,
    'managed-services': <FaCogs />
  };

  const cloudServices = cloudCategory 
    ? cloudCategory.services.map(service => ({
        ...service,
        category: cloudCategory.category,
        icon: iconMap[service.id] || service.icon
      }))
    : [];

  return (
    <section className="section bg-white py-24">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our AWS Cloud Services</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            AWS Cloud Solutions tailored to meet your specific business needs and challenges.
          </p>
        </motion.div>
        <motion.div
          className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {cloudServices.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </motion.div>
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <Link
            to="/services/aws-services"
            className="inline-block px-8 py-3 bg-primary text-white font-medium rounded-md hover:bg-primary/90 transition-colors duration-300 shadow-md hover:shadow-lg"
          >
            View All Services
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => (
  <section className="section relative py-24">
    <div className="absolute inset-0 z-0 opacity-10">
      <img
        src={testimonialBg}
        alt="Testimonial Background"
        className="w-full h-full object-cover"
      />
    </div>
    <div className="container mx-auto px-4 relative z-10">

      {contentData.testimonials.slice(0, 1).map((testimonial, index) => (
        <motion.div
          key={index}
          variants={slideUp}
          className="h-full"
        >
          <TestimonialCarousel
            quote={testimonial.feedback}
            name={testimonial.name}
            title=""
            company={testimonial.company}
          />
        </motion.div>
      ))}

    </div>
  </section>
);

const CaseStudiesSection = () => {
  const caseStudies = [
    {
      title: "Enterprise Cloud Migration",
      company: "Financial Services",
      description: "How we helped a leading financial institution migrate their legacy systems to a secure cloud environment, reducing costs by 35%."
    },
    {
      title: "DevOps Implementation",
      company: "Healthcare",
      description: "Implementing CI/CD pipelines and DevOps practices for a healthcare provider, improving deployment frequency by 300%."
    },
    {
      title: "Cloud Security Overhaul",
      company: "E-commerce",
      description: "Comprehensive security assessment and implementation for a major e-commerce platform, ensuring PCI compliance and data protection."
    }
  ];

  return (
    <section className="section bg-gray-50 py-24">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Recent Case Studies</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore how we've helped businesses like yours achieve success with cloud solutions.
          </p>
        </motion.div>
        <motion.div
          className="grid gap-8 md:grid-cols-3"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {caseStudies.map((study, index) => (
            <motion.div
              key={index}
              variants={slideUp}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="h-full"
            >
              <CaseStudyCard
                title={study.title}
                company={study.company}
                description={study.description}
                imageSrc={caseStudyImg}
              />
            </motion.div>
          ))}
        </motion.div>
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <Link
            to="/case-studies"
            className="inline-block px-8 py-3 border border-primary text-primary font-medium rounded-md hover:bg-primary hover:text-white transition-all duration-300"
          >
            View All Case Studies
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="relative py-24">
    <div className="absolute inset-0 z-0">
      <img
        src={ctaBanner}
        alt="CTA Banner"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black opacity-70"></div>
    </div>
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-4xl mx-auto text-center text-white">
        <motion.h2
          className="text-3xl md:text-4xl font-bold mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Ready to Transform Your Business with Cloud Solutions?
        </motion.h2>
        <motion.p
          className="text-xl mb-8 text-white/90"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          Let's discuss how our cloud expertise can help you achieve your business goals.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-primary text-white font-medium rounded-md hover:bg-primary/90 transition-colors duration-300 shadow-lg hover:shadow-xl"
          >
            Schedule a Consultation
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);

// Enhanced Blog Section
const BlogSection = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Latest{' '}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Insights
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Stay updated with the latest trends, tips, and insights in cloud technology
          </p>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {contentData.blogs.slice(0, 3).map((blog, index) => (
            <BlogCard
              key={blog.id}
              blog={blog}
              index={index}
              enableHoverEffects={index < 2}
            />
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <Link
            to="/blog"
            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl"
          >
            <span>Read More Articles</span>
            <FaArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Main HomePage component
const HomePage = () => {
  const seoData = {
    title: "AWS Cloud & DevOps Services",
    description: `${contentData.company.name} provides expert AWS cloud migration, DevOps automation, and AI solutions. Transform your business with our proven cloud services and achieve ${contentData.company.client_satisfaction} client satisfaction.`,
    keywords: "AWS Cloud Services, Cloud Migration, DevOps, Cloud Security, Data Analytics, AI Solutions, Azure, GCP, Cloud Computing, Managed Services"
  };

  return (
    <>
      <SEO {...seoData} />

      {/* Header Headline Section - Replicating style from second photo */}
      <section className="relative pt-32 pb-16 flex items-center overflow-hidden bg-white text-gray-900 border-b border-gray-50">
        <div className="container relative z-10 px-6 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-6xl"
          >
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[1] mb-8">
              <span className="block bg-gradient-to-r from-blue-700 via-purple-600 to-blue-500 bg-clip-text text-transparent pb-2 mt-2">
                Dhyon Technologies
              </span> 
              <span className="text-slate-900 block mt-2 text-5xl md:text-6xl lg:text-7xl transition-all duration-500 hover:text-blue-900">
                Innovation. Intelligence. Impact.
              </span>
            </h1>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="relative pl-6 border-l-4 border-blue-600/30"
            >
              <p className="text-xl md:text-2xl text-slate-600 max-w-3xl leading-relaxed italic">
                Transforming businesses with AI-powered software, cloud excellence, and startup-focused digital solutions.
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-gray-400 flex flex-col items-center gap-2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-xs uppercase tracking-widest text-slate-400">Scroll</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-slate-300 to-transparent"></div>
        </motion.div>
      </section>

      {/* Hero Section */}
      <HeroSimple />

      {/* Innovation Section moved from About Page */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-24"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-200/50 rounded-full px-6 py-3 mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <FaLightbulb className="text-blue-600 mr-2" />
              <span className="text-gray-700 font-medium">Our Foundation</span>
            </motion.div>
            
            <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Driven by </span>
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                Innovation.
              </span>
              <span className="block text-slate-900">Powered by </span>
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                Technology.
              </span>
            </h2>
            
            <p className="text-xl text-slate-600 max-w-5xl mx-auto leading-relaxed">
              Helping startups and enterprises grow faster with smart software and cloud solutions.
            </p>
          </motion.div>

          {/* First Section - AWS Cloud Solutions (Modified: Image Top with Title Overlay) */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Image with Title Overlay */}
            <div className="relative rounded-3xl overflow-hidden mb-12 group h-[250px] md:h-[450px] shadow-2xl max-w-5xl mx-auto">
              <img 
                src="/assets/Business1.png" 
                alt="AWS Cloud Solutions"
                className="w-full h-full object-cover transform transition-transform duration-1000 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-slate-900/20 flex items-center justify-center px-12">
                <div className="w-full max-w-7xl flex flex-col md:flex-row items-center justify-between gap-8">
                  <motion.h2 
                    className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-2xl md:text-left flex-1"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                  >
                    Empowering Businesses
                  </motion.h2>
                  <motion.h2 
                    className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-2xl md:text-right flex-1"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                  >
                    Through AWS Cloud Solutions
                  </motion.h2>
                </div>
              </div>
            </div>

            {/* Content Below */}
            <div className="space-y-12">
              <motion.p 
                className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-5xl mx-auto text-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                We enable organisations to unlock the full potential of AWS by delivering secure, scalable, and high-performance cloud solutions that drive innovation, agility, and sustainable growth in the digital era.
              </motion.p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { title: "AWS-Certified Excellence", text: "Deliver enterprise-grade cloud architectures built on AWS best practices to ensure reliability, security, and scalability." },
                  { title: "Cloud-Led Digital Transformation", text: "Accelerate modernization through AWS-native services, automation, and data-driven solutions designed for performance and innovation." },
                  { title: "Customer-Centric Cloud Support", text: "Provide proactive AWS support and optimization to ensure seamless operations, cost efficiency, and continuous improvement." }
                ].map((point, i) => (
                  <motion.div 
                    key={i} 
                    className="p-8 bg-white rounded-3xl border border-slate-100 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + (i * 0.2) }}
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 text-blue-600">
                      <FaCheckCircleFilled className="text-2xl" />
                    </div>
                    <h4 className="text-xl font-bold text-slate-800 mb-4">{point.title}</h4>
                    <p className="text-slate-600 leading-relaxed">{point.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Second Section - Software Innovation (Modified: Image Top with Title Overlay) */}
          <motion.div 
            className="mb-32"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Image with Title Overlay */}
            <div className="relative rounded-3xl overflow-hidden mb-12 group h-[250px] md:h-[450px] shadow-2xl max-w-5xl mx-auto">
              <img 
                src="/assets/Business2.png" 
                alt="Software Innovation"
                className="w-full h-full object-cover transform transition-transform duration-1000 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-slate-900/20 flex items-center justify-center px-12">
                <div className="w-full max-w-7xl flex flex-col md:flex-row items-center justify-between gap-8">
                  <motion.h2 
                    className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-2xl md:text-left flex-1"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                  >
                    Powering Business Innovation
                  </motion.h2>
                  <motion.h2 
                    className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-2xl md:text-right flex-1"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                  >
                    Through Software
                  </motion.h2>
                </div>
              </div>
            </div>

            {/* Content Below */}
            <div className="space-y-12">
              <motion.p 
                className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-5xl mx-auto text-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                We design and build high-performance software solutions that help organisations innovate faster, scale efficiently, and stay competitive in a rapidly evolving digital landscape.
              </motion.p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { title: "Robust & Scalable Development", text: "Delivering secure, scalable, and high-quality software architectures tailored to business needs and long-term growth." },
                  { title: "Modern Engineering Practices", text: "Leveraging agile methodologies, cloud-native frameworks, and modern tech stacks to build reliable, future-ready applications." },
                  { title: "User-Centric & Performance-Driven", text: "Creating intuitive, high-performing digital experiences with a strong focus on usability, efficiency, and business impact." }
                ].map((point, i) => (
                  <motion.div 
                    key={i} 
                    className="p-8 bg-white rounded-3xl border border-slate-100 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + (i * 0.2) }}
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 text-blue-600">
                      <FaCheckCircleFilled className="text-2xl" />
                    </div>
                    <h4 className="text-xl font-bold text-slate-800 mb-4">{point.title}</h4>
                    <p className="text-slate-600 leading-relaxed">{point.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Third Section - Startup Business (Modified: Image Top with Title Overlay) */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Image with Title Overlay */}
            <div className="relative rounded-3xl overflow-hidden mb-12 group h-[250px] md:h-[450px] shadow-2xl max-w-5xl mx-auto">
              <img 
                src="/assets/Business3.png" 
                alt="Startup Business"
                className="w-full h-full object-cover transform transition-transform duration-1000 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-slate-900/20 flex items-center justify-center px-12">
                <div className="w-full max-w-7xl flex flex-col md:flex-row items-center justify-between gap-8">
                  <motion.h2 
                    className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-2xl md:text-left flex-1"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                  >
                    From Idea
                  </motion.h2>
                  <motion.h2 
                    className="text-3xl md:text-5xl font-bold text-white leading-tight drop-shadow-2xl md:text-right flex-1"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                  >
                    to Impact
                  </motion.h2>
                </div>
              </div>
            </div>

            {/* Content Below */}
            <div className="space-y-12">
              <div className="text-center">
                <div className="inline-block px-4 py-2 bg-blue-50 rounded-full text-blue-600 font-semibold text-sm mb-4">
                  For startup business
                </div>
                <motion.p 
                  className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-5xl mx-auto"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  We empower startups to transform ideas into market-ready products through strategic guidance, agile development, and scalable technology.
                </motion.p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { title: "Concept & MVP Development", text: "Convert vision into functional, user-ready digital products." },
                  { title: "Technology Enablement", text: "Leverage modern tools and platforms to build robust solutions." },
                  { title: "Scale & Optimize", text: "Enhance performance, reliability, and market readiness." }
                ].map((point, i) => (
                  <motion.div 
                    key={i} 
                    className="p-8 bg-white rounded-3xl border border-slate-100 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + (i * 0.2) }}
                  >
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 text-white font-bold">
                      {i + 1}
                    </div>
                    <h4 className="text-xl font-bold text-slate-800 mb-4">{point.title}</h4>
                    <p className="text-slate-600 leading-relaxed">{point.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section removed */}



      {/* AWS Partner Section */}
      <AWSPartnerSection />

      {/* Partners Section */}
      <PartnersSection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Case Studies Section */}
      <CaseStudiesSection />

      {/* Call to Action Section */}
      <CTASection />

      {/* Blog Section */}
      <BlogSection />

      {/* Support Chat */}
      <SupportChat />
    </>
  );
};

export default HomePage; 